import { useMemo, useState } from "react";

/**
 * `useElementRef` — a ref that PUBLISHES the element it is attached to.
 *
 * ## The defect it exists to make unrepresentable
 *
 * A `useRef` object attached with `ref={obj}` is a silent channel: React writes
 * `obj.current` during commit and tells nobody. An effect that paints into that
 * element therefore has NO honest dependency for "the node I write into was
 * destroyed and a different one took its place", so it is forced to key on
 * content instead:
 *
 * ```ts
 * useEffect(() => { ref.current?.replaceChildren(...); }, [html]);   // ← blind
 * ```
 *
 * That works only while every remount happens to coincide with a content change.
 * The moment a surface is swapped out and back with the SAME content — a
 * conditional branch, a tab, a "return to the latest version of this document" —
 * the freshly mounted element is empty, no dependency moved, and nothing ever
 * paints it. The document is simply gone, with no error and no failing render.
 *
 * Keying the effect on the ELEMENT closes that class of bug for good: element
 * identity is the true dependency of an imperative paint, and it moves exactly
 * when — and only when — the paint has to happen again.
 *
 * ## The shape
 *
 * `ref` is a callback ref (React calls it on attach/detach, which is what makes
 * `element` live) that ALSO carries `.current`, so imperative readers —
 * `focusAnchor`, hit-testing, measurement — keep working unchanged and it drops
 * into any `RefObject<T | null>` prop position. Its identity is stable for the
 * lifetime of the component, so React attaches it once per mounted node rather
 * than detaching and re-attaching on every render.
 *
 * ```tsx
 * const { ref, element } = useElementRef<HTMLDivElement>();
 * useEffect(() => {
 *   if (!element) return;
 *   element.innerHTML = html;          // runs for EVERY mounted element
 * }, [element, html]);
 * return <div ref={ref} />;
 * ```
 */
export type LiveElementRef<T extends Element> = React.RefObject<T | null> &
  ((node: T | null) => void);

export interface UseElementRefResult<T extends Element> {
  /** Attach to the element (`<div ref={ref} />`). Stable across renders. */
  readonly ref: LiveElementRef<T>;
  /** The currently attached element, or `null` while nothing is mounted. Use it
   *  as the dependency of any effect that writes into that element. */
  readonly element: T | null;
}

export function useElementRef<T extends Element>(): UseElementRefResult<T> {
  const [element, setElement] = useState<T | null>(null);
  const ref = useMemo<LiveElementRef<T>>(() => {
    const attach = (node: T | null): void => {
      (attach as unknown as { current: T | null }).current = node;
      // Identity-compare: React re-invokes a ref callback on every remount, and
      // an unconditional set would schedule a no-op render each time.
      setElement((prev) => (prev === node ? prev : node));
    };
    (attach as unknown as { current: T | null }).current = null;
    return attach as unknown as LiveElementRef<T>;
  }, []);
  return { ref, element };
}
